/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { SnackBarContext } from '@/components/SnackBarContext';
import { useLabels } from '@/hooks/useLabels';
import { localStorage } from '@/utils/localStorage';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSubmitFeedbackMutation } from '../api/postSubmitFeedback';
import type { ReviewFeedbackAction, ReviewFeedbackState } from '../interface';

type SubmitFeedbackContextValue = {
  submitFeedback: (reviewID: string, action: ReviewFeedbackAction) => Promise<void>;
  localFeedback: ReviewFeedbackState | undefined;
};

const SubmitFeedbackContext = createContext<SubmitFeedbackContextValue>({
  submitFeedback: () => {
    throw new Error('useSubmitFeedback must be used within the SubmitFeedbackContextProvider');
  },
  localFeedback: undefined,
});

type SubmitFeedbackContextProviderProps = {
  children: ReactNode;
};

const labelMap = {
  feedbackSubmitted: 'label_reviews_feedbackSubmitted',
  feedbackError: 'label_reviews_feedbackError',
};

export function SubmitFeedbackContextProvider({ children }: SubmitFeedbackContextProviderProps) {
  const labels = useLabels(labelMap);
  const { showSnackBar } = useContext(SnackBarContext);
  const { mutateAsync } = useSubmitFeedbackMutation();

  const [localFeedback, setLocalFeedback] = useState<ReviewFeedbackState>(() => ({
    helpfulness: {},
    inappropriate: {},
  }));

  useEffect(() => {
    const data = localStorage.getItem('reviewFeedback');
    if (data) {
      try {
        const currentFeedback = JSON.parse(data) as ReviewFeedbackState;
        setLocalFeedback(currentFeedback);
      } catch {}
    }
  }, []);

  const submitFeedback = useCallback(
    async (reviewID: string, action: ReviewFeedbackAction) => {
      const feedbackType = action === 'report' ? 'inappropriate' : 'helpfulness';
      const vote =
        action === 'positive' ? 'Positive' : action === 'negative' ? 'Negative' : undefined;

      try {
        const data = await mutateAsync({
          contentId: reviewID,
          contentType: 'review',
          feedbackType,
          vote,
        });

        if (data.HasErrors) {
          showSnackBar(labels.feedbackError);
          return;
        }

        showSnackBar(labels.feedbackSubmitted);
        setLocalFeedback((prevState) => {
          const newState = {
            ...prevState,
            [feedbackType]: {
              ...prevState[feedbackType],
              [reviewID]: true,
            },
          };
          localStorage.setItem('reviewFeedback', JSON.stringify(newState));
          return newState;
        });
      } catch {
        showSnackBar(labels.feedbackError);
      }
    },
    [labels.feedbackError, labels.feedbackSubmitted, mutateAsync, showSnackBar]
  );

  const value = useMemo(() => ({ submitFeedback, localFeedback }), [submitFeedback, localFeedback]);

  return <SubmitFeedbackContext.Provider value={value}>{children}</SubmitFeedbackContext.Provider>;
}

export const useSubmitFeedback = (): SubmitFeedbackContextValue => {
  const value = useContext(SubmitFeedbackContext);
  return value;
};
